// What is typed into the New Quotation form, and the sums worked out from it.
import { jordanDate } from '@serb/shared';
import { addDays } from '../../../lib/dates';
import { SAMPLE_DATA } from '../../../lib/sample';
import type { Shipment } from '../../shipments/sample';

export type CargoLine = {
  description: string;
  containers: string;
  dimensions: string;
  chargeable: string;
  gross: string;
};
export type Charge = { item: string; units: 'per kg' | 'per shipment' | 'per container'; unitPrice: string };

export type QuoteForm = {
  customer: string;
  contact: string;
  email: string;
  number: string;
  date: string;
  validity: string;
  relatedSrb: string;
  origin: string;
  destination: string;
  transit: string;
  mode: string;
  type: string;
  incoterm: string;
  carrier: string;
  cutOff: string;
  sailing: string;
  cargo: CargoLine[];
  charges: Charge[];
  conditions: string;
};

export const MODES = ['Air Freight', 'FCL', 'LCL'];
export const INCOTERMS = ['EXW', 'FCA', 'FOB', 'CIF', 'CPT', 'CIP', 'DAP', 'DDP'];
export const UNITS: Charge['units'][] = ['per kg', 'per shipment', 'per container'];
export const NEW_CARGO: CargoLine = { description: '', containers: '', dimensions: '', chargeable: '', gross: '' };
export const NEW_CHARGE: Charge = { item: '', units: 'per shipment', unitPrice: '' };

const EMPTY: QuoteForm = {
  customer: '',
  contact: '',
  email: '',
  number: '',
  date: '',
  validity: '',
  relatedSrb: '',
  origin: '',
  destination: '',
  transit: '',
  mode: '',
  type: '',
  incoterm: '',
  carrier: '',
  cutOff: '',
  sailing: '',
  cargo: [NEW_CARGO],
  charges: [NEW_CHARGE],
  conditions: '',
};

/** A new form. While sample data is on, it starts filled in so the design can be judged. */
export function initialForm(): QuoteForm {
  const today = jordanDate();
  const base = { ...EMPTY, date: today, validity: addDays(today, 7) };
  if (!SAMPLE_DATA) return base;
  return {
    ...base,
    customer: 'ABC Trading',
    contact: 'Mr. Ahmad Khalil',
    email: 'ahmad@abctrading.com',
    number: 'QUOTE-26-1305-4',
    relatedSrb: 'SRB-711',
    origin: 'Guangzhou (CAN)',
    destination: 'Amman (AMM)',
    transit: '7 days',
    mode: 'Air Freight',
    type: 'General Cargo',
    incoterm: 'EXW',
    carrier: 'MU / G9-XY',
    cutOff: addDays(today, 2),
    sailing: addDays(today, 5),
    cargo: [
      {
        description: '3 packages (general cargo)',
        containers: '',
        dimensions: '',
        chargeable: '222.25',
        gross: '222.25',
      },
    ],
    charges: [
      { item: 'Air Freight (MU / G9-XY)', units: 'per kg', unitPrice: '5.70' },
      { item: 'Origin Charges', units: 'per shipment', unitPrice: '100' },
      { item: 'Inland Transportation (CAN → AMM)', units: 'per shipment', unitPrice: '105' },
    ],
  };
}

/** Picking a related shipment fills in what we already know about it */
export function fillFromShipment(form: QuoteForm, s: Shipment): QuoteForm {
  return {
    ...form,
    relatedSrb: s.srb,
    customer: s.customer,
    origin: `${s.from.city} (${s.from.code})`,
    destination: `${s.to.city} (${s.to.code})`,
    mode: s.service.startsWith('Air') ? 'Air Freight' : s.mode,
    type: s.cargo,
    incoterm: s.incoterm,
  };
}

const num = (text: string) => Number(text.replace(/,/g, '')) || 0;

/** The weight that "per kg" prices are multiplied by */
export const chargeableWeight = (f: QuoteForm) => f.cargo.reduce((sum, c) => sum + num(c.chargeable), 0);

/** What one charge comes to: per kg × weight, per container × containers, per shipment × 1 */
export function chargeTotal(c: Charge, f: QuoteForm): number {
  const unit = num(c.unitPrice);
  if (c.units === 'per kg') return unit * chargeableWeight(f);
  if (c.units === 'per container') return unit * f.cargo.reduce((sum, l) => sum + (num(l.containers) || 0), 0);
  return unit;
}

export const totalPrice = (f: QuoteForm) => f.charges.reduce((sum, c) => sum + chargeTotal(c, f), 0);
